import { useState, useEffect, lazy, Suspense } from "react";
import { Layout } from "./components/Layout";
import { Modal } from "shared/components";
import { useMakeDecisionController } from "./controller";
import { Views } from "./types";
import type { FormData, FormDataErrors } from "./types";
import { MakeDecisionView } from "./views/MakeDecisionView";
import { DecisionSettingsView } from "./views/DecisionSettingsView";
import { ConditionsView } from "./views/ConditionsView";
import { EmailView } from "./views/EmailView";
import api from "api/landlord/address";
import { generateAdditionalEmailText } from "./utils";
import { conditionsByAdverseActionLetterType } from "./constants";
import { AdverseActionLetterType } from "api/landlord/templates";
import { SuccessView } from "./views/SuccessView";
import { getErrorMessage } from "utils/error";
import { invalidateNeedsLockboxCounts } from "shared/utils/units-cache";
import { toast } from "react-toastify";
import { Application, DecisionAction } from "shared/types";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Loader } from "shared/components/Loader";
import { useAuthContext } from "providers/Auth";
import { Template } from "types/landlord/verificationTemplate";
import { Applicant } from "types/application";
import { activityLogsQueryKey } from "shared/queries/useActivityLogsController";
import { useSettingsController } from "views/settings-integration/controller";
import { ApprovedAppWarningModal } from "./components/ApprovedAppWarningModal";

const ResidencyConditionsView = lazy(
  () => import("./views/ResidencyConditionsView")
);

const getFullAddress = (
  address:
    | (Application & {
        verification_template: Template;
        score: number;
      })
    | undefined
) => {
  if (!address) return "";
  const parts = [
    address.unit.title ? `${address.unit.title}, ` : "",
    address.property?.address.address1
      ? `${address.property.address.address1}, `
      : "",
    address.property?.address.city ? `${address.property.address.city} ` : "",
    address.property?.address.state_or_province
      ? `${address.property.address.state_or_province}, `
      : "",
    address.property?.address.postal_code ?? ""
  ].join("");

  return parts || "--";
};

type ControllerReturn = ReturnType<typeof useMakeDecisionController>;
type ApplicationMeta = ControllerReturn["applicationMeta"];
type Flow = ControllerReturn["flow"];

interface ViewContentProps {
  applicationMeta: ApplicationMeta;
  view: Views;
  onClose: () => void;
  formData: FormData;
  flow: Flow;
  updateFormData: (updates: Partial<FormData>) => void;
  sendApplicantsToPMS: Record<string, boolean>;
  disablePmsForOtherDecisions: boolean;
  showDetailedConditionsView: boolean;
  setShowDetailedConditionsView: React.Dispatch<React.SetStateAction<boolean>>;
  formDataErrors: FormDataErrors;
  successViewFormData: FormData;
  applicationId: string;
}

const ViewContent = ({
  applicationMeta,
  view,
  onClose,
  formData,
  flow,
  updateFormData,
  sendApplicantsToPMS,
  disablePmsForOtherDecisions,
  showDetailedConditionsView,
  setShowDetailedConditionsView,
  formDataErrors,
  successViewFormData,
  applicationId
}: ViewContentProps) => {
  if (applicationMeta.isFetching)
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader />
      </div>
    );

  switch (view) {
    case Views.MakeDecision:
      return (
        <MakeDecisionView
          onClose={onClose}
          applicationId={applicationMeta.data?.id}
          requiresManualExecution={
            applicationMeta.data?.requires_manual_or_conditional_execution
          }
          recommendationMethod={
            applicationMeta.data?.verification_template.recommendation_method
          }
          applicants={applicationMeta.data?.applicants || []}
          recommendation={applicationMeta.data?.recommendation}
          templateCriteriaSettings={{
            conditional_approval_range_end: Number(
              applicationMeta.data?.verification_template.screening_criteria
                ?.conditional_approval_range_end
            ),
            decline_range_end: Number(
              applicationMeta.data?.verification_template.screening_criteria
                ?.decline_range_end
            )
          }}
          sendApplicantsToPMS={sendApplicantsToPMS}
          formData={formData}
          score={Number(applicationMeta.data?.score)}
          view={view}
          flow={flow}
          updateFormData={updateFormData}
        />
      );
    case Views.DecisionSettings:
      return (
        <DecisionSettingsView
          formData={formData}
          updateFormData={updateFormData}
          view={view}
          flow={flow}
          address={getFullAddress(applicationMeta.data)}
          disableAddApplicantsToPms={
            disablePmsForOtherDecisions ||
            (!applicationMeta.data?.property?.pms_id &&
              !applicationMeta.data?.property?.external_id)
          }
          unlistUnitTiming={
            applicationMeta.data?.verification_template
              .automatically_unlist_unit_timing
          }
        />
      );
    case Views.Conditions:
      return (
        <ConditionsView
          formData={formData}
          updateFormData={updateFormData}
          view={view}
          flow={flow}
          adverseActionTemplate={
            applicationMeta.data?.verification_template
              .adverse_action_letter_type
          }
          recommendation={applicationMeta.data?.recommendation}
          showDetailedConditionsView={showDetailedConditionsView}
          setShowDetailedConditionsView={setShowDetailedConditionsView}
          ruleReasons={applicationMeta.data?.rule_reasons}
          recommendationMethod={
            applicationMeta.data?.verification_template.recommendation_method
          }
          ruleCriteria={
            applicationMeta.data?.verification_template.rule_criteria
          }
        />
      );
    case Views.ResidencyConditions:
      return (
        <Suspense
          fallback={
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          }
        >
          <ResidencyConditionsView
            formData={formData}
            updateFormData={updateFormData}
            view={view}
            flow={flow}
            formDataErrors={formDataErrors}
          />
        </Suspense>
      );
    case Views.Email:
      return (
        <EmailView
          property={applicationMeta.data?.property}
          verificationTemplate={applicationMeta.data?.verification_template}
          applicants={applicationMeta.data?.applicants || []}
          address={getFullAddress(applicationMeta.data)}
          conditionalApprovalEmailMessage={
            applicationMeta.data?.conditional_approval_email_message
          }
          approvalEmailMessage={applicationMeta.data?.approval_email_message}
          declinedEmailMessage={applicationMeta.data?.declined_email_message}
          formData={formData}
          updateFormData={updateFormData}
          view={view}
          flow={flow}
        />
      );
    case Views.Success:
      return (
        <SuccessView
          formData={successViewFormData}
          address={getFullAddress(applicationMeta.data)}
          applicationId={applicationId}
          applicants={
            (applicationMeta.data?.applicants as unknown[] | undefined as
              | Applicant[]
              | undefined) || []
          }
        />
      );
    default:
      return null;
  }
};

export const MakeDecisionModal = ({
  opened,
  onClose,
  applicationId
}: {
  opened: boolean;
  onClose: () => void;
  applicationId: string;
}) => {
  const { type, permissions } = useAuthContext();
  const queryClient = useQueryClient();
  const {
    applicationMeta,
    formData,
    view,
    onNextView,
    setPrevView,
    flow,
    updateFormData,
    resetConditions,
    validateFormData,
    formDataErrors,
    propertyCredential,
    isDecisionTriggeredPmsPush
  } = useMakeDecisionController(applicationId);
  const { currentSettings } = useSettingsController();
  const { pms: pmsSettings } = currentSettings;

  const [successViewFormData, setSuccessViewFormData] = useState(formData);
  const [showDetailedConditionsView, setShowDetailedConditionsView] =
    useState(true);
  const [showApprovedAppWarning, setShowApprovedAppWarning] = useState(false);

  useEffect(() => {
    if (showDetailedConditionsView)
      resetConditions(
        applicationMeta.data?.rule_reasons,
        applicationMeta.data?.recommendation
      );
  }, [showDetailedConditionsView]);

  const { mutate: executeDecision, isPending: isSubmitting } = useMutation({
    mutationKey: ["APPLICATION", applicationId, "UPDATE"],
    mutationFn: async () => {
      const conditions = formData.conditions;
      if (formData.additionalCondition)
        conditions.push(formData.additionalCondition);

      const letterType =
        formData.decision === DecisionAction.Approve
          ? AdverseActionLetterType.Generic
          : applicationMeta.data?.verification_template
              ?.adverse_action_letter_type || AdverseActionLetterType.Generic;

      const emailMessage = formData.sendNotifications
        ? formData.additionalEmailText
        : generateAdditionalEmailText(
            formData,
            applicationMeta.data?.applicants || [],
            getFullAddress(applicationMeta.data),
            conditionsByAdverseActionLetterType[letterType],
            letterType,
            applicationMeta.data?.conditional_approval_email_message
          );

      const body = {
        send_applicants_to_pms: type.isPMS
          ? formData.addApplicantsToPMS
          : undefined,
        note_message: formData.decisionNotes,
        email_message: emailMessage,
        skip_sending_email: !formData.sendNotifications
      };

      await (formData.decision === DecisionAction.Approve ||
      formData.decision === DecisionAction.ConditionalApprove
        ? api.approveApplication(applicationId, {
            ...body,
            conditions:
              formData.decision === DecisionAction.Approve
                ? undefined
                : conditions
          })
        : api.rejectApplication(applicationId, {
            ...body,
            reasons: conditions
          }));

      const unitMagicLink = applicationMeta.data?.unit.magic_link;
      if (
        permissions.canChangeUnitAvailability &&
        formData.decision !== DecisionAction.Decline &&
        (formData.unlistUnit || formData.disallowApplications) &&
        unitMagicLink?.id &&
        (unitMagicLink.active || applicationMeta.data?.unit.listed)
      ) {
        const newActiveStatus = formData.disallowApplications
          ? false
          : unitMagicLink.active;
        const newListedStatus = formData.unlistUnit
          ? false
          : Boolean(applicationMeta.data?.unit.listed);

        await api.updateMagicLink(unitMagicLink.id, {
          active: newActiveStatus,
          listed: newListedStatus
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: activityLogsQueryKey(applicationId)
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      // Approving can unlist the unit, which drops it from the lockbox counts.
      invalidateNeedsLockboxCounts(queryClient);

      if (formData.decision === DecisionAction.Decline) onClose();
      else {
        setSuccessViewFormData(formData);
        onNextView();
      }
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const onSubmit = async () => {
    if (!validateFormData()) return;

    const isApproval =
      formData.decision === DecisionAction.Approve ||
      formData.decision === DecisionAction.ConditionalApprove;

    if (isApproval)
      try {
        const warningResponse =
          await api.checkApprovedAppWarning(applicationId);

        if (warningResponse.warning) {
          setShowApprovedAppWarning(true);
          return;
        }
      } catch {
        // If the check fails, proceed with the decision
      }

    executeDecision();
  };

  const disablePmsForOtherDecisions = !isDecisionTriggeredPmsPush;

  const resolvePushSetting = (key: string) => {
    const raw = propertyCredential?.settings?.[key] ?? pmsSettings[key];
    return raw === true || raw === "true";
  };

  const sendApplicantsToPMS = {
    [DecisionAction.Approve]:
      isDecisionTriggeredPmsPush &&
      resolvePushSetting("pms_prospect_creation_on_approve"),

    [DecisionAction.ConditionalApprove]:
      isDecisionTriggeredPmsPush &&
      resolvePushSetting("pms_prospect_creation_on_conditional_approve"),

    [DecisionAction.Decline]:
      isDecisionTriggeredPmsPush &&
      resolvePushSetting("pms_prospect_creation_on_decline")
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-h-[min(90vh,840px)] h-full max-w-[880px] w-full animate-appear border border-alpha-100 shadow-lg"
    >
      <Layout
        view={view}
        onClose={onClose}
        onNext={onNextView}
        onPrev={setPrevView}
        formData={formData}
        onSubmit={onSubmit}
        showDetailedConditionsView={showDetailedConditionsView}
        setShowDetailedConditionsView={setShowDetailedConditionsView}
        recommendation={applicationMeta.data?.recommendation}
        recommendationMethod={
          applicationMeta.data?.verification_template.recommendation_method
        }
        formDataErrors={formDataErrors}
        isButtonLoading={isSubmitting}
      >
        <ViewContent
          applicationMeta={applicationMeta}
          view={view}
          onClose={onClose}
          formData={formData}
          flow={flow}
          updateFormData={updateFormData}
          sendApplicantsToPMS={sendApplicantsToPMS}
          disablePmsForOtherDecisions={disablePmsForOtherDecisions}
          showDetailedConditionsView={showDetailedConditionsView}
          setShowDetailedConditionsView={setShowDetailedConditionsView}
          formDataErrors={formDataErrors}
          successViewFormData={successViewFormData}
          applicationId={applicationId}
        />
      </Layout>
      <ApprovedAppWarningModal
        isOpen={showApprovedAppWarning}
        onClose={() => setShowApprovedAppWarning(false)}
        onConfirm={() => {
          setShowApprovedAppWarning(false);
          executeDecision();
        }}
        isSubmitting={isSubmitting}
      />
    </Modal>
  );
};
